import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Shift, ShiftSearchParams } from '../../services/shiftsApi';

export interface ShiftsState {
  // Data
  items: Shift[];
  selectedItem: Shift | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  formData: {
    Name: string;
    StartTime: string;
    EndTime: string;
    Active: boolean;
  };
  
  // Validation
  validationErrors: string[];
  
  // Search and Filter
  searchParams: ShiftSearchParams;
  
  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Bulk Operations
  selectedIds: number[];
  bulkOperationLoading: boolean;
  
  // Success Messages
  successMessage: string;
}

const initialState: ShiftsState = {
  // Data
  items: [],
  selectedItem: null,
  
  // UI State
  loading: false,
  error: null,
  
  // Form State
  formData: {
    Name: '',
    StartTime: '',
    EndTime: '',
    Active: true,
  },
  
  // Validation
  validationErrors: [],
  
  // Search and Filter
  searchParams: {
    page: 1,
    pageSize: 10,
    search: '',
    active: undefined,
    sortBy: 'Name',
    sortOrder: 'asc',
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  
  // Bulk Operations
  selectedIds: [],
  bulkOperationLoading: false,
  
  // Success Messages
  successMessage: '',
};

const shiftsSlice = createSlice({
  name: 'shifts',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data management
    setItems: (state, action: PayloadAction<Shift[]>) => {
      state.items = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<Shift | null>) => {
      state.selectedItem = action.payload;
    },
    addItem: (state, action: PayloadAction<Shift>) => {
      state.items.unshift(action.payload);
    },
    updateItem: (state, action: PayloadAction<Shift>) => {
      const index = state.items.findIndex(item => item.Id === action.payload.Id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.selectedItem?.Id === action.payload.Id) {
        state.selectedItem = action.payload;
      }
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.Id !== action.payload);
      if (state.selectedItem?.Id === action.payload) {
        state.selectedItem = null;
      }
    },
    
    // Form management
    setFormData: (state, action: PayloadAction<Partial<ShiftsState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    resetFormData: (state) => {
      state.formData = initialState.formData;
    },
    
    // Validation
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    
    // Search and filter
    setSearchParams: (state, action: PayloadAction<Partial<ShiftSearchParams>>) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    resetSearchParams: (state) => {
      state.searchParams = initialState.searchParams;
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<ShiftsState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    
    // Bulk operations
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    addSelectedId: (state, action: PayloadAction<number>) => {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
      }
    },
    removeSelectedId: (state, action: PayloadAction<number>) => {
      state.selectedIds = state.selectedIds.filter(id => id !== action.payload);
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    setBulkOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkOperationLoading = action.payload;
    },
    
    // Success messages
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = '';
    },
    
    // Reset state
    resetShiftsState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setFormData,
  resetFormData,
  setValidationErrors,
  clearValidationErrors,
  setSearchParams,
  resetSearchParams,
  setPagination,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  resetShiftsState,
} = shiftsSlice.actions;

// Saga action types
export const FETCH_SHIFTS = 'shifts/fetchShifts';
export const FETCH_SHIFT_BY_ID = 'shifts/fetchShiftById';
export const CREATE_SHIFT = 'shifts/createShift';
export const UPDATE_SHIFT = 'shifts/updateShift';
export const DELETE_SHIFT = 'shifts/deleteShift';
export const SEARCH_SHIFTS = 'shifts/searchShifts';
export const BULK_ACTIVATE = 'shifts/bulkActivate';
export const BULK_DEACTIVATE = 'shifts/bulkDeactivate';
export const BULK_DELETE = 'shifts/bulkDelete';
export const EXPORT_TO_CSV = 'shifts/exportToCSV';

// Saga action creators
export const fetchShifts = (params?: ShiftSearchParams) => ({ type: FETCH_SHIFTS, payload: params });
export const fetchShiftById = (id: number) => ({ type: FETCH_SHIFT_BY_ID, payload: id });
export const createShift = (shift: Omit<Shift, 'Id'>) => ({ type: CREATE_SHIFT, payload: shift });
export const updateShift = (id: number, shift: Partial<Shift>) => ({ type: UPDATE_SHIFT, payload: { id, shift } });
export const deleteShift = (id: number) => ({ type: DELETE_SHIFT, payload: id });
export const searchShifts = (params: ShiftSearchParams) => ({ type: SEARCH_SHIFTS, payload: params });
export const bulkActivate = (ids: number[]) => ({ type: BULK_ACTIVATE, payload: ids });
export const bulkDeactivate = (ids: number[]) => ({ type: BULK_DEACTIVATE, payload: ids });
export const bulkDelete = (ids: number[]) => ({ type: BULK_DELETE, payload: ids });
export const exportToCSV = (params?: ShiftSearchParams) => ({ type: EXPORT_TO_CSV, payload: params });

export default shiftsSlice.reducer; 